import {
  ChangeEventHandler,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Flex, InputProps, Spinner, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';

import { Deal } from '../../../../gql/dealGql';
import { FinancialInfo, OptionTypeEnum, getTotalTaxes } from '../../../../gql/financialInfoGql';
import { ProductSlug } from '../../../../gql/generated/graphql';

import NumberInput from '../../../shared/NumberInput';

import Package from './Package';

import { isClosed } from '../../../../constants/dealStates';
import { PermissionEnum } from '../../../../constants/permissions';
import { useDealPackageConfigs } from '../../../../hooks/useDealPackageConfigs';
import { useDealProducts } from '../../../../hooks/useDealProducts';
import useDebounce from '../../../../hooks/useDebounce';
import { usePackageConfigs } from '../../../../hooks/usePackageConfigs';
import { usePackagePayments } from '../../../../hooks/usePackagePayments';
import { useProductAvailability } from '../../../../hooks/useProductAvailability';
import { useProductsCatalog } from '../../../../hooks/useProductsCatalog';
import { DealActionsEnum, DealContext } from '../../../../libs/DealContext';
import { AbilityContext } from '../../../../libs/contextLib';
import { isNumber } from '../../../../libs/utils';
import {
  DEFAULT_PACKAGE_CONFIGS,
  PackageConfig,
  PersistedPackageConfig,
  ProductAvailability,
  findFirstAvailableNonGapPackage,
  getGapProductAvailability,
  getProductSignature,
  normalizeProductSlugs,
} from '../../../../types/products';
import { DebouncedSave } from '../../DealInfoBuyoutForm';
import { paymentOptionsTypes } from '../../types';

type PaymentOptionsProps = {
  debounceSave: DebouncedSave;
  isEditingWithPermission: boolean;
  customHandleChange?: ChangeEventHandler<HTMLInputElement | HTMLSelectElement>;
  // Lets sibling components (the VSC/Calc "Save" buttons in ProductsSection)
  // re-persist the currently selected package to deal_products.
  persistSelectedPackageRef?: MutableRefObject<(() => Promise<void>) | null>;
};

const rateAndTermInputProps: InputProps = {
  bgColor: 'white',
  borderColor: 'gray.600',
  _hover: {
    borderColor: 'gray.600',
  },
  fontSize: {
    base: 'lg',
    '2xl': 'xl',
    '3xl': '2xl',
    '4xl': '3xl',
  },
  minH: '1.5em',
};

const toPersistedPackageConfigPayload = (
  packageConfigs: Array<Pick<PackageConfig, 'slotIndex' | 'selectedProductSlugs'>>,
): PersistedPackageConfig[] =>
  packageConfigs.map((config) => ({
    slotIndex: config.slotIndex,
    selectedProductSlugs: normalizeProductSlugs(config.selectedProductSlugs),
  }));

const isPresent = <T,>(value: T | null | undefined): value is T => value != null;

const toPackageConfigState = (persistedConfigs: PersistedPackageConfig[]): PackageConfig[] => {
  const persistedBySlot = new Map(
    persistedConfigs.map((config) => [
      config.slotIndex,
      normalizeProductSlugs(config.selectedProductSlugs),
    ]),
  );

  return DEFAULT_PACKAGE_CONFIGS.map((defaultConfig) => ({
    ...defaultConfig,
    selectedProductSlugs: persistedBySlot.get(defaultConfig.slotIndex) ?? [
      ...defaultConfig.selectedProductSlugs,
    ],
  }));
};

const arePersistedPackageConfigsEqual = (
  left: PersistedPackageConfig[],
  right: PersistedPackageConfig[],
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((config) => {
    const otherConfig = right.find((item) => item.slotIndex === config.slotIndex);
    if (!otherConfig) {
      return false;
    }

    return (
      getProductSignature(config.selectedProductSlugs) ===
      getProductSignature(otherConfig.selectedProductSlugs)
    );
  });
};

type SavePackageSelectionOptions = {
  persistedConfigsOverride?: PersistedPackageConfig[];
  skipImmediateFormSync?: boolean;
};

const PaymentOptions = ({
  debounceSave,
  isEditingWithPermission,
  customHandleChange,
  persistSelectedPackageRef,
}: PaymentOptionsProps) => {
  const abilities = useContext(AbilityContext);
  const { deal, dispatch } = useContext(DealContext);

  // Fetch products catalog
  const { products } = useProductsCatalog();

  // Auto-included PEN products (maintenance, ALW) come back with
  // is_user_selectable = false and are managed entirely by the backend, so they
  // must never appear in the package product picker. The full catalog is still
  // used below for the save/id-lookup paths so auto-included rows resolve.
  const selectableProducts = useMemo(
    () => products.filter((product) => product.is_user_selectable),
    [products],
  );

  // Persisted package configuration per deal
  const {
    configs: persistedPackageConfigs,
    queryLoading: isPackageConfigsLoading,
    saveConfigs: savePackageConfigs,
    refetch: refetchPackageConfigs,
  } = useDealPackageConfigs();

  // Manage package configurations (4 cards)
  const { configs, hydrateConfigs, toggleProduct, updateAllConfigs } =
    usePackageConfigs(selectableProducts);

  const hasHydratedPackageConfigs = useRef(false);
  const [persistedConfigSnapshot, setPersistedConfigSnapshot] = useState<PersistedPackageConfig[]>(
    () => toPersistedPackageConfigPayload(DEFAULT_PACKAGE_CONFIGS),
  );
  const persistedConfigPayload = useMemo(
    () =>
      persistedPackageConfigs.length > 0
        ? toPersistedPackageConfigPayload(persistedPackageConfigs)
        : toPersistedPackageConfigPayload(DEFAULT_PACKAGE_CONFIGS),
    [persistedPackageConfigs],
  );

  useEffect(() => {
    if (hasHydratedPackageConfigs.current || isPackageConfigsLoading) {
      return;
    }

    setPersistedConfigSnapshot(persistedConfigPayload);
    if (persistedPackageConfigs.length > 0) {
      hydrateConfigs(persistedPackageConfigs);
    }
    hasHydratedPackageConfigs.current = true;
  }, [hydrateConfigs, isPackageConfigsLoading, persistedConfigPayload, persistedPackageConfigs]);

  // Get payment calculations for packages
  const {
    getOptionForPackage,
    getPackageResult,
    selectedPackageSlotIndex,
    isLoading: isPackagePaymentsLoading,
    isDebouncing: isPackagePaymentsDebouncing,
    refetch: refetchPackagePayments,
  } = usePackagePayments(configs);

  // Product availability checking
  const { isProductAvailable } = useProductAvailability();

  // Deal products management (new system)
  const { dealProducts, saveProductsFromCatalog, refetch: refetchDealProducts } = useDealProducts();

  const {
    initialValues,
    values,
    handleChange: formikHandleChange,
    setValues,
  } = useFormikContext<Deal>();
  const handleChange = customHandleChange ?? formikHandleChange;
  const currentConfigPayload = useMemo(() => toPersistedPackageConfigPayload(configs), [configs]);
  const hasDirtyPackageConfigs = useMemo(
    () => !arePersistedPackageConfigsEqual(currentConfigPayload, persistedConfigSnapshot),
    [currentConfigPayload, persistedConfigSnapshot],
  );

  const [optimisticSelectedSlotIndex, setOptimisticSelectedSlotIndex] = useState<number | null>(
    null,
  );
  const effectiveSelectedSlotIndex = optimisticSelectedSlotIndex ?? selectedPackageSlotIndex;
  const persistedSelectedSlotIndex = deal.selected_package_slot_index ?? null;

  // Tracks the last option applied by the sync effect so we can skip no-op updateForm calls.
  // Without this, a config toggle in a non-selected package triggers: configs change → query
  // re-fires → sync effect calls updateForm → setValues → auto-save effect fires dealInfoUpsert.
  const prevSyncOptionRef = useRef<{
    slotIndex: number;
    option: paymentOptionsTypes;
  } | null>(null);

  useEffect(() => {
    hasHydratedPackageConfigs.current = false;
    setOptimisticSelectedSlotIndex(null);
    setPersistedConfigSnapshot(toPersistedPackageConfigPayload(DEFAULT_PACKAGE_CONFIGS));
    prevSyncOptionRef.current = null;
  }, [values.id]);

  useEffect(() => {
    if (
      optimisticSelectedSlotIndex != null &&
      selectedPackageSlotIndex === optimisticSelectedSlotIndex
    ) {
      setOptimisticSelectedSlotIndex(null);
    }
  }, [optimisticSelectedSlotIndex, selectedPackageSlotIndex]);

  const isDisabled = isClosed(values.state) && !isEditingWithPermission;
  const isSelectionInFlight = optimisticSelectedSlotIndex != null;

  const getCurrentDealProductBySlug = useCallback(
    (slug: ProductSlug) =>
      deal.deal_products?.find((dealProduct) => dealProduct.product_catalog?.slug === slug) ??
      dealProducts.find((dealProduct) => dealProduct.product_catalog?.slug === slug),
    [deal.deal_products, dealProducts],
  );

  const parseNumericValue = useCallback((value: number | string | null | undefined) => {
    const parsedValue =
      value == null || value === '' ? Number.NaN : Number.parseFloat(String(value));

    return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : undefined;
  }, []);

  const resolveFormikFirstProductValue = useCallback(
    (
      value: number | string | null | undefined,
      slug: ProductSlug,
      field: 'price' | 'cost',
    ): number | undefined => {
      const parsedValue = parseNumericValue(value);
      if (parsedValue != null) {
        return parsedValue;
      }

      return getCurrentDealProductBySlug(slug)?.[field] ?? undefined;
    },
    [getCurrentDealProductBySlug, parseNumericValue],
  );

  const resolveDealProductFirstValue = useCallback(
    (
      value: number | string | null | undefined,
      slug: ProductSlug,
      field: 'price' | 'cost',
    ): number | undefined => {
      const dealProductValue = getCurrentDealProductBySlug(slug)?.[field];
      if (dealProductValue != null) {
        return dealProductValue;
      }

      return parseNumericValue(value);
    },
    [getCurrentDealProductBySlug, parseNumericValue],
  );

  // Memoize mapOptionToValues to avoid recreating on every render
  const mapOptionToValues = useCallback(
    (option: paymentOptionsTypes, currentValues: Deal, updateOptionType = false): Deal => {
      return {
        ...currentValues,
        financial_info: {
          ...currentValues.financial_info,
          option_type: updateOptionType
            ? (option.name as OptionTypeEnum)
            : currentValues.financial_info.option_type,
          profit: option.profit,
          amount_financed: option.amountFinanced,
          setter_commission: option.setterCommission,
          closer_commission: option.closerCommission,
          payment: option.sellPayment,
          reserve: option.reserve,
          tempUserEnteredReserve: option.reserve,
          totalTaxAmount: getTotalTaxes({
            ...currentValues.financial_info,
            option_type: option.name as OptionTypeEnum,
          }),
        },
      };
    },
    [],
  );

  // Stable updateForm using functional setState pattern
  const updateForm = useCallback(
    (
      option: paymentOptionsTypes,
      config: { updateOptionType?: boolean; autosave?: boolean } = {
        updateOptionType: false,
        autosave: false,
      },
    ) => {
      // Do nothing if the `amountFinanced` is 0
      if (option.amountFinanced === 0) {
        return;
      }

      setValues((currentValues) => {
        const mappedValues = mapOptionToValues(option, currentValues, config.updateOptionType);

        // Check if we need to autosave
        const paymentOptionsNeverSaved =
          !initialValues.financial_info.amount_financed &&
          mappedValues.financial_info.amount_financed;

        const keysToCheck: (keyof FinancialInfo)[] = [
          'amount_financed',
          'option_type',
          'profit',
          'setter_commission',
          'closer_commission',
          'payment',
          'reserve',
          'base_tax_amount',
          'warranty_tax_amount',
        ];

        const valuesAreDifferent = keysToCheck.some((key) => {
          const mappedValue = mappedValues.financial_info[key];
          if (!mappedValue) {
            return false;
          }

          const initialValue = initialValues.financial_info[key];

          const mappedFixed =
            typeof mappedValue === 'number' ? Number(mappedValue.toFixed(2)) : mappedValue;
          const initialFixed =
            typeof initialValue === 'number' ? Number(initialValue.toFixed(2)) : initialValue;

          return mappedFixed !== initialFixed;
        });

        if (config.autosave || paymentOptionsNeverSaved || valuesAreDifferent) {
          // Use setTimeout to avoid calling debounceSave during render
          setTimeout(() => debounceSave({ newValues: mappedValues }), 0);
        }

        return mappedValues;
      });
    },
    [debounceSave, initialValues.financial_info, mapOptionToValues, setValues],
  );

  const rollbackToPersistedPackageState = useCallback(() => {
    updateAllConfigs(toPackageConfigState(persistedConfigSnapshot));
    setOptimisticSelectedSlotIndex(null);
    refetchPackagePayments().catch(() => undefined);
  }, [persistedConfigSnapshot, refetchPackagePayments, updateAllConfigs]);

  const debouncedSavePackageConfigs = useDebounce(async (nextConfigs: PackageConfig[]) => {
    const payload = toPersistedPackageConfigPayload(nextConfigs);

    try {
      await savePackageConfigs(payload);
      setPersistedConfigSnapshot(payload);
      await Promise.all([refetchPackageConfigs(), refetchPackagePayments()]);
    } catch {
      rollbackToPersistedPackageState();
      toast.error('Error saving package configuration, please refresh the page');
    }
  }, 600);

  const savePackageSelection = useCallback(
    async (
      packageConfig: PackageConfig,
      option: paymentOptionsTypes,
      options: SavePackageSelectionOptions = {},
    ) => {
      const selectedProducts = products.filter((product) =>
        packageConfig.selectedProductSlugs.includes(product.slug),
      );
      const nextPersistedConfigs =
        options.persistedConfigsOverride ??
        (hasDirtyPackageConfigs ? currentConfigPayload : undefined);
      const shouldPersistConfigs = nextPersistedConfigs != null;

      const penRateData = {
        vsc: {
          rateId: values.financial_info.pen_vsc_rate_id ?? undefined,
        },
        gap: {
          rateId: values.financial_info.pen_gap_rate_id ?? undefined,
        },
      };
      const currentVscProduct = getCurrentDealProductBySlug(ProductSlug.Vsc);
      const financialInfoPrices = {
        vscPrice: resolveFormikFirstProductValue(
          values.financial_info.vsc_price,
          ProductSlug.Vsc,
          'price',
        ),
        vscCost: resolveFormikFirstProductValue(
          values.financial_info.vsc_cost,
          ProductSlug.Vsc,
          'cost',
        ),
        vscTerm: values.financial_info.vsc_term ?? currentVscProduct?.term ?? undefined,
        vscType: values.financial_info.vsc_type ?? currentVscProduct?.coverage_type ?? undefined,
        gapPrice: resolveFormikFirstProductValue(
          values.financial_info.gap_price,
          ProductSlug.Gap,
          'price',
        ),
        gapCost: resolveFormikFirstProductValue(
          values.financial_info.gap_cost,
          ProductSlug.Gap,
          'cost',
        ),
        tireWheelPrice: resolveDealProductFirstValue(
          values.tire_wheel_price,
          ProductSlug.TireWheel,
          'price',
        ),
        tireWheelCost: resolveDealProductFirstValue(
          values.tire_wheel_cost,
          ProductSlug.TireWheel,
          'cost',
        ),
        keyReplacementPrice: resolveDealProductFirstValue(
          values.key_replacement_price,
          ProductSlug.KeyReplacement,
          'price',
        ),
        keyReplacementCost: resolveDealProductFirstValue(
          values.key_replacement_cost,
          ProductSlug.KeyReplacement,
          'cost',
        ),
      };

      if (shouldPersistConfigs) {
        debouncedSavePackageConfigs.cancel();
      }

      setOptimisticSelectedSlotIndex(packageConfig.slotIndex);

      try {
        const result = await saveProductsFromCatalog(
          selectedProducts,
          penRateData,
          financialInfoPrices,
          {
            selectedPackageSlotIndex: packageConfig.slotIndex,
            packageConfigs: nextPersistedConfigs,
          },
        );

        if (shouldPersistConfigs && nextPersistedConfigs) {
          setPersistedConfigSnapshot(nextPersistedConfigs);
        }

        const updatedOptionType =
          (result.data?.updateDealProductsV2?.financial_info?.option_type as OptionTypeEnum) ??
          (option.name as OptionTypeEnum);
        const updatedDealProducts = result.data?.updateDealProductsV2?.deal_products
          ?.filter(isPresent)
          .map((updatedDealProduct) => {
            const existingDealProduct = deal.deal_products?.find(
              (dealProduct) =>
                dealProduct.product_catalog_id === updatedDealProduct.product_catalog_id,
            );
            const productCatalog =
              products.find((product) => product.id === updatedDealProduct.product_catalog_id) ??
              existingDealProduct?.product_catalog ??
              null;

            return {
              ...existingDealProduct,
              ...updatedDealProduct,
              product_catalog: productCatalog,
            };
          });

        dispatch({
          type: DealActionsEnum.UpdateDeal,
          payload: {
            selected_package_slot_index: packageConfig.slotIndex,
            ...(updatedDealProducts ? { deal_products: updatedDealProducts } : {}),
          },
        });
        dispatch({
          type: DealActionsEnum.DeepUpdateDeal,
          payload: {
            financial_info: {
              option_type: updatedOptionType,
            },
          },
        });

        if (!options.skipImmediateFormSync) {
          updateForm(option, { updateOptionType: true, autosave: false });
        }

        await Promise.all([
          refetchDealProducts(),
          refetchPackagePayments(),
          shouldPersistConfigs ? refetchPackageConfigs() : Promise.resolve(),
        ]);

        return result;
      } catch (error) {
        rollbackToPersistedPackageState();
        throw error;
      }
    },
    [
      currentConfigPayload,
      debouncedSavePackageConfigs,
      deal.deal_products,
      dealProducts,
      dispatch,
      getCurrentDealProductBySlug,
      hasDirtyPackageConfigs,
      products,
      refetchPackageConfigs,
      refetchDealProducts,
      refetchPackagePayments,
      resolveDealProductFirstValue,
      resolveFormikFirstProductValue,
      rollbackToPersistedPackageState,
      saveProductsFromCatalog,
      updateForm,
      values.financial_info.gap_cost,
      values.financial_info.gap_price,
      values.financial_info.pen_gap_rate_id,
      values.financial_info.pen_vsc_rate_id,
      values.financial_info.vsc_cost,
      values.financial_info.vsc_price,
      values.financial_info.vsc_term,
      values.financial_info.vsc_type,
      values.key_replacement_cost,
      values.key_replacement_price,
      values.tire_wheel_cost,
      values.tire_wheel_price,
    ],
  );

  const handleToggleProduct = useCallback(
    (packageId: string, productSlug: ProductSlug) => {
      if (isDisabled) {
        return;
      }

      const nextConfigs = toggleProduct(packageId, productSlug);
      if (!nextConfigs.length) {
        return;
      }

      debouncedSavePackageConfigs(nextConfigs);
    },
    [debouncedSavePackageConfigs, isDisabled, toggleProduct],
  );

  // Calculate derived values during render (rerender-derived-state-no-effect)
  const missingInfo = useMemo(() => {
    return (
      !values.car.year ||
      !values.car.vin ||
      !values.car.mileage ||
      !isNumber(values.car.retail_book_value) ||
      !isNumber(values.car.payoff.vehicle_payoff) ||
      !isNumber(values.financial_info.sell_rate) ||
      !isNumber(values.financial_info.term) ||
      !values.customer.address?.state
    );
  }, [
    values.car.year,
    values.car.vin,
    values.car.mileage,
    values.car.retail_book_value,
    values.car.payoff.vehicle_payoff,
    values.financial_info.sell_rate,
    values.financial_info.term,
    values.customer.address?.state,
  ]);

  const getGapAvailabilityForPackage = useCallback(
    (packageConfig: PackageConfig): ProductAvailability => {
      const packageResult = getPackageResult(packageConfig);

      return getGapProductAvailability({
        amountFinanced: packageResult?.amountFinanced,
        retailBookValue: values.car.retail_book_value,
        missingInfo,
      });
    },
    [getPackageResult, missingInfo, values.car.retail_book_value],
  );

  const getProductAvailabilityForPackage = useCallback(
    (packageConfig: PackageConfig, productSlug: ProductSlug): ProductAvailability => {
      if (productSlug === ProductSlug.Gap) {
        return getGapAvailabilityForPackage(packageConfig);
      }

      return isProductAvailable(productSlug);
    },
    [getGapAvailabilityForPackage, isProductAvailable],
  );

  // A package is selectable when all products are available and the payment option is calculated.
  const checkPackageAvailability = useCallback(
    (packageConfig: typeof configs[0]) => {
      const hasProducts = packageConfig.selectedProductSlugs.length > 0;
      if (!hasProducts) {
        return true;
      }

      if (missingInfo) {
        return false;
      }

      const packageResult = getPackageResult(packageConfig);
      if (!packageResult || packageResult.amountFinanced === 0) {
        return false;
      }

      const includesGap = packageConfig.selectedProductSlugs.includes(ProductSlug.Gap);
      if (includesGap && !getGapAvailabilityForPackage(packageConfig).available) {
        return false;
      }

      const hasUnavailableProduct = packageConfig.selectedProductSlugs.some(
        (slug) => !getProductAvailabilityForPackage(packageConfig, slug).available,
      );
      if (hasUnavailableProduct) {
        return false;
      }

      return true;
    },
    [getGapAvailabilityForPackage, getPackageResult, getProductAvailabilityForPackage, missingInfo],
  );

  // Handle clicking a package card to select it
  const handleClick = useCallback(
    (packageConfig: typeof configs[0]) => {
      if (isDisabled || !checkPackageAvailability(packageConfig)) {
        return;
      }

      if (optimisticSelectedSlotIndex != null) {
        return;
      }

      if (packageConfig.slotIndex === effectiveSelectedSlotIndex) {
        return;
      }

      const option = getOptionForPackage(packageConfig);
      savePackageSelection(packageConfig, option).catch(() => {
        toast.error('Error saving package products, please refresh the page');
      });
    },
    [
      checkPackageAvailability,
      effectiveSelectedSlotIndex,
      getOptionForPackage,
      isDisabled,
      optimisticSelectedSlotIndex,
      savePackageSelection,
    ],
  );

  // Re-persist the currently selected package to deal_products. The VSC grid /
  // Calc buttons (ProductsSection.onRatesLoaded) only update Formik; without
  // this their freshly-picked values (price/cost/term/coverage/penRateId) are
  // never written. We re-submit the FULL current package — not just the changed
  // product — because updateDealProductsV2 validates the submitted product set
  // against the selected slot's signature and rejects a mismatch.
  const persistSelectedPackage = useCallback(async () => {
    if (effectiveSelectedSlotIndex == null) {
      return;
    }
    const currentConfig = configs.find((config) => config.slotIndex === effectiveSelectedSlotIndex);
    if (!currentConfig) {
      return;
    }
    await savePackageSelection(currentConfig, getOptionForPackage(currentConfig));
  }, [configs, effectiveSelectedSlotIndex, getOptionForPackage, savePackageSelection]);

  // Publish the latest persist fn to the shared ref so sibling components can
  // call it. Re-runs whenever persistSelectedPackage changes (i.e. when the
  // Formik product values change), keeping the ref closed over current values.
  useEffect(() => {
    const ref = persistSelectedPackageRef;
    if (!ref) {
      return undefined;
    }
    ref.current = persistSelectedPackage;
    return () => {
      ref.current = null;
    };
  }, [persistSelectedPackage, persistSelectedPackageRef]);

  const isPackageDataReady = !isPackagePaymentsLoading && !isPackagePaymentsDebouncing;

  useEffect(() => {
    if (isDisabled || !isPackageDataReady || optimisticSelectedSlotIndex != null) {
      return;
    }

    if (persistedSelectedSlotIndex == null) {
      return;
    }

    const selectedConfig = configs.find(
      (config) => config.slotIndex === persistedSelectedSlotIndex,
    );
    if (!selectedConfig) {
      return;
    }

    const selectedIncludesGap = selectedConfig.selectedProductSlugs.includes(ProductSlug.Gap);
    if (!selectedIncludesGap || getGapAvailabilityForPackage(selectedConfig).available) {
      return;
    }

    const fallbackConfig = findFirstAvailableNonGapPackage(configs, checkPackageAvailability);

    if (!fallbackConfig || fallbackConfig.slotIndex === persistedSelectedSlotIndex) {
      const gapRemovedConfig = {
        ...selectedConfig,
        selectedProductSlugs: normalizeProductSlugs(
          selectedConfig.selectedProductSlugs.filter((slug) => slug !== ProductSlug.Gap),
        ),
      };

      if (
        gapRemovedConfig.selectedProductSlugs.length === selectedConfig.selectedProductSlugs.length
      ) {
        return;
      }

      const nextConfigs = configs.map((config) =>
        config.slotIndex === selectedConfig.slotIndex ? gapRemovedConfig : config,
      );
      const nextPersistedConfigs = toPersistedPackageConfigPayload(nextConfigs);

      updateAllConfigs(nextConfigs);

      savePackageSelection(gapRemovedConfig, getOptionForPackage(selectedConfig), {
        persistedConfigsOverride: nextPersistedConfigs,
        skipImmediateFormSync: true,
      }).catch(() => {
        toast.error('Error saving package products, please refresh the page');
      });
      return;
    }

    const fallbackOption = getOptionForPackage(fallbackConfig);
    if (fallbackOption.amountFinanced === 0) {
      return;
    }

    savePackageSelection(fallbackConfig, fallbackOption).catch(() => {
      toast.error('Error saving package products, please refresh the page');
    });
  }, [
    checkPackageAvailability,
    configs,
    getGapAvailabilityForPackage,
    getOptionForPackage,
    isDisabled,
    isPackageDataReady,
    optimisticSelectedSlotIndex,
    updateAllConfigs,
    persistedSelectedSlotIndex,
    savePackageSelection,
  ]);

  // Sync form values when package payment values recalculate (sell_rate, term, etc.).
  useEffect(() => {
    if (!isPackageDataReady || optimisticSelectedSlotIndex != null) {
      return;
    }

    if (effectiveSelectedSlotIndex == null) {
      return;
    }

    const currentConfig = configs.find((config) => config.slotIndex === effectiveSelectedSlotIndex);
    if (!currentConfig) {
      return;
    }

    // Avoid reapplying a GAP option while GAP is ineligible.
    if (
      currentConfig.selectedProductSlugs.includes(ProductSlug.Gap) &&
      !getGapAvailabilityForPackage(currentConfig).available
    ) {
      return;
    }

    const option = getOptionForPackage(currentConfig);
    if (option.amountFinanced === 0) {
      return;
    }

    // Skip if the selected package's option hasn't actually changed.
    // This prevents a config toggle in a non-selected package from triggering
    // updateForm → setValues → auto-save effect → dealInfoUpsert.
    const prev = prevSyncOptionRef.current;
    if (
      prev &&
      prev.slotIndex === effectiveSelectedSlotIndex &&
      prev.option.name === option.name &&
      prev.option.amountFinanced === option.amountFinanced &&
      prev.option.sellPayment === option.sellPayment &&
      prev.option.profit === option.profit &&
      prev.option.setterCommission === option.setterCommission &&
      prev.option.closerCommission === option.closerCommission &&
      prev.option.reserve === option.reserve
    ) {
      return;
    }
    prevSyncOptionRef.current = { slotIndex: effectiveSelectedSlotIndex, option };

    updateForm(option, { updateOptionType: true });
  }, [
    configs,
    effectiveSelectedSlotIndex,
    getGapAvailabilityForPackage,
    getOptionForPackage,
    isPackageDataReady,
    optimisticSelectedSlotIndex,
    updateForm,
  ]);

  const toggleHandlers = useMemo(
    () =>
      Object.fromEntries(
        configs.map((config) => [
          config.id,
          (slug: ProductSlug) => handleToggleProduct(config.id, slug),
        ]),
      ),
    [configs, handleToggleProduct],
  );

  const clickHandlers = useMemo(
    () => Object.fromEntries(configs.map((config) => [config.id, () => handleClick(config)])),
    [configs, handleClick],
  );

  const isNoProtectionPackage = (packageConfig: typeof configs[0]) =>
    packageConfig.selectedProductSlugs.length === 0;

  return (
    <Flex direction="column" bgColor="brightGray">
      <Flex direction="row" alignSelf="center" mb={3} mt={3} fontSize="sm">
        <Flex mr={2} ml={3} mt={2}>
          <Text mr={2} mt={3} whiteSpace="nowrap">
            Interest Rate
          </Text>
          <NumberInput
            name="financial_info.sell_rate"
            isPercentage
            isDisabled={isDisabled || !abilities.has(PermissionEnum.EditSellRate)}
            additionalHandleChange={handleChange}
            _input={{
              ...rateAndTermInputProps,
              maxW: '4.5em',
            }}
          />
        </Flex>
        <Flex mr={2} ml={3} mt={2}>
          <Text mr={2} mt={3} whiteSpace="nowrap">
            Term
          </Text>
          <NumberInput
            name="financial_info.term"
            isDisabled={isDisabled}
            additionalHandleChange={handleChange}
            _input={{
              ...rateAndTermInputProps,
              maxW: '3.5em',
            }}
          />
        </Flex>
      </Flex>
      <Flex gap={2} alignSelf="center" px={3} maxW="full" overflowX="auto">
        {isPackageConfigsLoading && !hasHydratedPackageConfigs.current ? (
          <Flex justify="center" align="center" w="full" py={10}>
            <Spinner size="lg" />
          </Flex>
        ) : (
          configs.map((config) => {
            const option = getOptionForPackage(config);
            const packageAvailable = checkPackageAvailability(config);
            const isNoProtection = isNoProtectionPackage(config);

            return (
              <Package
                key={config.id}
                name={config.displayName || 'Package'}
                option={option}
                isSelected={config.slotIndex === effectiveSelectedSlotIndex}
                isDisabled={
                  isDisabled ||
                  (isSelectionInFlight && config.slotIndex !== optimisticSelectedSlotIndex)
                }
                isAvailable={packageAvailable}
                onClick={clickHandlers[config.id]}
                packageConfig={config}
                products={selectableProducts}
                onToggleProduct={toggleHandlers[config.id]}
                getProductAvailability={(slug) => getProductAvailabilityForPackage(config, slug)}
                showReserveEdit={
                  isNoProtection && !isDisabled && abilities.has(PermissionEnum.EditReserve)
                }
                debounceSave={isNoProtection ? debounceSave : undefined}
              />
            );
          })
        )}
      </Flex>
    </Flex>
  );
};

export default PaymentOptions;
